import { ArrowUp, Loader2, Square } from "lucide-react";
import * as React from "react";
import { Children, type FormEvent, type KeyboardEvent, useState } from "react";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button, type ButtonProps } from "~/components/ui/button";
import type { TextareaProps } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";

/**
 * Text-only port of the shadcn AI-Elements PromptInput composer.
 *
 * The upstream component bundles file attachments, a model picker, screenshot
 * and speech actions, which pull in the `ai` SDK, `nanoid` and several extra
 * registry components. None of that is wired up yet (the chat request is
 * text-only), so this vendored copy keeps the same structure/API but trims it
 * to a self-resizing textarea, a toolbar slot, and a submit button. Attachment
 * pieces will be added back when the backend supports multimodal chat.
 */

export type ChatStatus = "ready" | "submitted" | "streaming" | "error";

export type PromptInputMessage = {
  text: string;
};

export type PromptInputProps = Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  "onSubmit"
> & {
  onSubmit: (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>
  ) => void;
};

export const PromptInput = ({
  className,
  onSubmit,
  children,
  ...props
}: PromptInputProps) => {
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const text = (new FormData(form).get("message") as string | null) ?? "";
    onSubmit({ text }, event);
  };

  return (
    <form
      className={cn(
        "flex w-full flex-col gap-1 rounded-xl border border-input bg-background p-1.5 shadow-xs transition-colors focus-within:ring-1 focus-within:ring-ring",
        className
      )}
      onSubmit={handleSubmit}
      {...props}
    >
      {children}
    </form>
  );
};

export type PromptInputTextareaProps = TextareaProps;

export const PromptInputTextarea = ({
  className,
  onKeyDown,
  ...props
}: PromptInputTextareaProps) => {
  const [isComposing, setIsComposing] = useState(false);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) {
      return;
    }
    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }
    if (isComposing || event.nativeEvent.isComposing) {
      return;
    }
    event.preventDefault();
    const form = event.currentTarget.form;
    const submitButton = form?.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    );
    if (submitButton?.disabled) {
      return;
    }
    form?.requestSubmit();
  };

  return (
    <AutoResizeTextarea
      autoComplete="off"
      name="message"
      rows={1}
      className={cn(
        "min-h-[40px] max-h-[200px] overflow-y-auto border-0 bg-transparent px-2 py-1.5 text-sm shadow-none focus-visible:ring-0 focus-visible:outline-hidden",
        className
      )}
      onCompositionStart={() => setIsComposing(true)}
      onCompositionEnd={() => setIsComposing(false)}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
};

export type PromptInputToolbarProps = React.HTMLAttributes<HTMLDivElement>;

export const PromptInputToolbar = ({
  className,
  ...props
}: PromptInputToolbarProps) => (
  <div
    className={cn("flex items-center justify-between gap-1 px-0.5", className)}
    {...props}
  />
);

export type PromptInputToolsProps = React.HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({
  className,
  ...props
}: PromptInputToolsProps) => (
  <div className={cn("flex items-center gap-1", className)} {...props} />
);

export type PromptInputButtonProps = ButtonProps;

export const PromptInputButton = ({
  variant = "ghost",
  size,
  className,
  ...props
}: PromptInputButtonProps) => {
  const resolvedSize =
    size ?? (Children.count(props.children) > 1 ? "sm" : "icon");

  return (
    <Button
      className={cn(className)}
      size={resolvedSize}
      type="button"
      variant={variant}
      {...props}
    />
  );
};

export type PromptInputSubmitProps = ButtonProps & {
  status?: ChatStatus;
};

export const PromptInputSubmit = ({
  className,
  variant = "default",
  size = "icon",
  status = "ready",
  children,
  ...props
}: PromptInputSubmitProps) => {
  let icon = <ArrowUp className="h-4 w-4" />;
  if (status === "submitted" || status === "streaming") {
    icon = <Loader2 className="h-4 w-4 animate-spin" />;
  } else if (status === "error") {
    icon = <Square className="h-4 w-4" />;
  }

  return (
    <Button
      className={cn("h-8 w-8", className)}
      size={size}
      type="submit"
      variant={variant}
      {...props}
    >
      {children ?? icon}
    </Button>
  );
};
